/* eslint-disable react/no-array-index-key, react/no-danger */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import axios from "axiosClient";
import axiosPayment from "axiosPaymentClient";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import PublicLayout from "components/PublicLayout";

import Cards from "@hasanboy93/react-cards-uz";
import "@hasanboy93/react-cards-uz/dist/es/styles-compiled.css";

import {
  formatCreditCardNumber,
  formatExpirationDate,
  formatOtp,
  formatPhone,
} from "utils/formatCardValues";
import { currencyFormatter } from "utils/formatter";
import { findWholePrice } from "utils/getWholePrice";
const momentTz = require("moment-timezone");

// GlobalPay is being trialled in production, so the button is shown only to
// these passports. Add to the list to widen the trial, or drop the check
// entirely to open it to everyone. Mirrors the existing gate on the Dev module.
const GLOBALPAY_TRIAL_PASSPORTS = ["AD2500372"];

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [price, setPrice] = useState(0);
  const [transactionId, setTransactionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryDetails, setEnquiryDetails] = useState({ exam: "TKT" });
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [examUi, setExamUi] = useState(null);
  const [enquiryId, setEnquiryId] = useState(searchParams.get("enquiryId"));
  const [isPost, setIsPost] = useState(false);
  const [forPayment, setForPayment] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [showPayButtons, setShowPayButtons] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [residence, setResidence] = useState("Uzbekistan");
  const [paymentMethod, setPaymentMethod] = useState("via");
  const [xaznaLoading, setXaznaLoading] = useState(false);
  const [uzumLoading, setUzumLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [validate, setValidate] = useState({
    emailState: "",
    passportState: "",
    pinppState: "",
  });
  const [dob, setDob] = useState(null);
  const [examDate, setExamDate] = useState(null);
  const [readyToPay, setReadyToPay] = useState(false);
  // undiscounted exam price, kept so the summary can show what was taken off
  const [basePrice, setBasePrice] = useState(0);
  const [promocodeInput, setPromocodeInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoReservationId, setPromoReservationId] = useState(null);
  const [promoAvailable, setPromoAvailable] = useState(false);
  const [cardPhone, setCardPhone] = useState("");

  const [cardDetails, setCardDetails] = useState({
    number: "",
    expiry: "",
    cvc: "",
    name: "",
    focus: "",
    phone: "",
    otp: "",
  });

  const { t, i18n } = useTranslation();

  // Single source of truth for the per-provider price breakdown. `baseAmount`
  // is the exam price after any promocode discount.
  const buildPrice = (baseAmount) => {
    const upayTotal = Math.ceil((baseAmount * 100) / 99);
    // Uzum and GlobalPay both take 3.5%, so both are grossed up the same way
    const uzumTotal = findWholePrice(baseAmount);
    return {
      upay: {
        exam: baseAmount,
        per: upayTotal - baseAmount,
        total: upayTotal,
      },
      via: {
        exam: baseAmount,
        per: baseAmount * 0.008,
        total: baseAmount * 0.008 + baseAmount,
      },
      uzum: {
        exam: baseAmount,
        per: uzumTotal - baseAmount,
        total: uzumTotal,
      },
      globalpay: {
        exam: baseAmount,
        per: uzumTotal - baseAmount,
        total: uzumTotal,
      },
    };
  };

  // Only offer the promocode box when something is actually on offer for this
  // exam/module - otherwise it is an input that can only ever fail.
  const checkPromoAvailability = async (exam, module) => {
    try {
      const result = await axiosPayment.get("/api/promocode-available", {
        params: { exam, module },
      });
      setPromoAvailable(Boolean(result.data?.available));
    } catch (error) {
      setPromoAvailable(false);
    }
  };

  const clearPromocode = (keepInput = false) => {
    setAppliedPromo(null);
    setPromoReservationId(null);
    if (!keepInput) setPromocodeInput("");
  };

  const handleApplyPromocode = async () => {
    const code = promocodeInput.trim();
    if (!code) return;
    if (!basePrice) return toast.error(t("promocodeSelectModule"));

    setPromoChecking(true);
    try {
      const result = await axiosPayment.post("/api/promocode/validate", {
        code,
        exam: enquiryDetails.exam || examUi,
        module: enquiryDetails.module,
        amount: basePrice,
        pinpp: enquiryDetails.pinpp,
        passportId: enquiryDetails.passportId,
      });

      if (!result.data.success) {
        clearPromocode(true);
        setPrice(buildPrice(basePrice));
        toast.error(
          result.data.message?.[i18n.language] || t("promocodeInvalid"),
        );
        return;
      }

      setAppliedPromo(result.data);
      setPrice(buildPrice(result.data.finalAmount));
      toast.success(t("promocodeApplied"));
    } catch (error) {
      clearPromocode(true);
      toast.error(t("promocodeInvalid"));
    } finally {
      setPromoChecking(false);
    }
  };

  const handleRemovePromocode = () => {
    clearPromocode();
    if (basePrice) setPrice(buildPrice(basePrice));
  };

  const globalpayEnabled = GLOBALPAY_TRIAL_PASSPORTS.includes(
    String(enquiryDetails?.passportId ?? "")
      .trim()
      .toUpperCase(),
  );

  const promoPayload = () =>
    appliedPromo
      ? {
          promocode: appliedPromo.code,
          exam: enquiryDetails.exam || examUi,
          module: enquiryDetails.module,
          paymentEnquiryId: enquiryId || enquiryDetails.enquiryId,
          candidate: {
            pinpp: enquiryDetails.pinpp,
            passportId: enquiryDetails.passportId,
            firstName: enquiryDetails.firstName,
            lastName: enquiryDetails.lastName,
            email: enquiryDetails.email,
            phoneNumber: enquiryDetails.phoneNumber,
          },
        }
      : {};

  const renderPromoBox = () => {
    if (!promoAvailable) return null;

    return (
      <div className="mb-3 mt-2">
        <Label className="mb-1" for="promocode">
          {t("promocode")}
        </Label>
        {appliedPromo ? (
          <div className="d-flex align-items-center flex-wrap" role="status">
            <span className="mr-2">
              <strong>{appliedPromo.code}</strong>:{" "}
              {currencyFormatter(`${appliedPromo.discountAmount}`, "UZS")}{" "}
              {t("promocodeOff")}
            </span>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={handleRemovePromocode}
            >
              {t("promocodeRemove")}
            </button>
          </div>
        ) : (
          <div className="d-flex">
            <Input
              id="promocode"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={promocodeInput}
              placeholder={t("promocodePlaceholder")}
              onChange={(e) =>
                setPromocodeInput(e.target.value.toUpperCase().trim())
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleApplyPromocode();
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary ml-2 text-nowrap"
              onClick={handleApplyPromocode}
              disabled={promoChecking || !promocodeInput}
            >
              {promoChecking && (
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  aria-hidden="true"
                />
              )}
              {t("promocodeApply")}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Original price, then what the promocode took off, then the provider fee.
  const renderPriceLines = () => {
    // providers that redirect (Xazna) have no fee row in `price`
    const breakdown = price?.[paymentMethod];
    if (!breakdown) return null;

    return (
      <dl className="price-summary">
        <div className="price-summary-row">
          <dt>{t("examPrice")}</dt>
          {/* while a code is applied, show the server's own figures so the
              original, the discount and the total always add up */}
          <dd>
            {currencyFormatter(
              `${appliedPromo ? appliedPromo.baseAmount : breakdown.exam}`,
              "UZS",
            )}
          </dd>
        </div>
        {appliedPromo && (
          <div className="price-summary-row text-success">
            <dt>{t("promocodeDiscount")}</dt>
            <dd>
              −{currencyFormatter(`${appliedPromo.discountAmount}`, "UZS")}
            </dd>
          </div>
        )}
        <div className="price-summary-row">
          <dt>{t("processingFee")}</dt>
          <dd>{currencyFormatter(`${breakdown.per}`, "UZS")}</dd>
        </div>
        <div className="price-summary-row price-summary-total">
          <dt>{t("totalPay")}</dt>
          <dd>{currencyFormatter(`${breakdown.total}`, "UZS")}</dd>
        </div>
      </dl>
    );
  };

  const renderSpinner = (show) =>
    show ? (
      <span
        className="spinner-border spinner-border-sm mr-2"
        aria-hidden="true"
      />
    ) : null;

  // One provider tile. `type="button"` keeps it from submitting the form
  // around it, and aria-pressed tells screen readers which one is chosen.
  const renderProviderButton = (method, content, busy = false) => (
    <button
      key={method}
      type="button"
      className={`btn btn-light service-button ${
        paymentMethod === method ? "active" : ""
      }`}
      aria-pressed={paymentMethod === method}
      aria-busy={busy}
      disabled={busy}
      onClick={() => handlePayMethodButton(method)}
    >
      {busy ? (
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      ) : (
        content
      )}
    </button>
  );

  const providerLogo = (name, file) => (
    <img
      src={`assets/logos/${file}`}
      alt={name}
      className="service-logo"
    />
  );

  const globalpayContent = (
    <>
      <span className="service-name">GlobalPay</span>
      <span className="service-schemes">Visa, Mastercard, UnionPay</span>
    </>
  );

  const renderInternationalProviders = () => (
    <div className="provider-group">
      <p className="provider-group-title" id="intl-providers">
        {t("intlCardTitle")}
      </p>
      <div
        className="provider-options"
        role="group"
        aria-labelledby="intl-providers"
      >
        {renderProviderButton("uzum", providerLogo("Uzum", "uzum.svg"), uzumLoading)}
        {globalpayEnabled &&
          renderProviderButton("globalpay", globalpayContent)}
      </div>
    </div>
  );

  const renderLocalProviders = () => (
    <div className="provider-group">
      <p className="provider-group-title" id="local-providers">
        UzCard / Humo
      </p>
      <div
        className="provider-options"
        role="group"
        aria-labelledby="local-providers"
      >
        {renderProviderButton(
          "xazna",
          providerLogo("Xazna", "xazna.svg"),
          xaznaLoading,
        )}
        {renderProviderButton("via", providerLogo("Via", "via.svg"))}
      </div>
    </div>
  );

  const examOptions = [
    { label: "IELTS", value: "IELTS" },
    { label: "TKT", value: "TKT" },
    { label: "Linguaskill", value: "Linguaskill" },
    { label: "Cambridge English Qualifications (CEQs)", value: "CEQs" },
  ];

  const residenceOptions = [
    { label: t("Uzbekistan"), value: "Uzbekistan" },
    { label: t("Other"), value: "Other" },
  ];

  // Enter in any field of the details form runs the step the user is on.
  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    if (!examUi) return;
    if (examUi !== "IELTS") return handleTktDetails();
    if (!showForm) return handleGetPin(e);
  };

  const handleCardSubmit = (e) =>
    isPost ? handlePostPayment(e) : handlePrePayment(e);

  const resetEnquiry = () => {
    setReadyToPay(false);
    setForPayment(true);
    setShowCard(false);
    setEnquiryDetails({ exam: examUi });
  };

  const providerName =
    paymentMethod === "upay"
      ? "UPay"
      : paymentMethod === "globalpay"
        ? "GlobalPay"
        : "Via";

  const handleInputChange = (evt) => {
    let { name, value } = evt.target;
    let newValue;
    if (name === "number") {
      value = formatCreditCardNumber(value);
    } else if (name === "expiry") {
      value = formatExpirationDate(value);
    } else if (name === "phone") {
      value = formatPhone(value);
    } else if (name === "otp") {
      value = formatOtp(value);
    }
    setCardDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputFocus = (evt) => {
    setCardDetails((prev) => ({ ...prev, focus: evt.target.name }));
  };
  const handleResidenceChange = (e) => {
    setResidence(e.value);
  };

  const handleValuesChange = (e) => {
    if (residence === "Uzbekistan" && e.target.name === "passportId") {
      setEnquiryDetails({
        ...enquiryDetails,
        [e.target.name]: e.target.value.toUpperCase().replace(" ", ""),
      });
    } else {
      let value = e.target.value;

      setEnquiryDetails({ ...enquiryDetails, [e.target.name]: value });
    }
    if (e.target.name === "passportId" && validate.passportState) {
      setValidate({ ...validate, passportState: "" });
    }
    setErrorMessage("");
  };

  const validatePassportId = (e) => {
    let rex = /[A-Z]{2}[0-9]{7}/;
    if (rex.test(e.target.value)) {
      setValidate({ ...validate, passportState: "has-success" });
    } else {
      setValidate({ ...validate, passportState: "has-danger" });
    }
  };

  const validatePinpp = (e) => {
    // PINPP should be exactly 14 digits
    let rex = /^\d{14}$/;
    if (rex.test(e.target.value)) {
      setValidate({ ...validate, pinppState: "has-success" });
    } else {
      setValidate({ ...validate, pinppState: "has-danger" });
    }
  };

  const validateEmail = (e) => {
    let rex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (rex.test(e.target.value)) {
      setValidate({ ...validate, emailState: "has-success" });
    } else {
      setValidate({ ...validate, emailState: "has-danger" });
    }
  };

  const regionOptions = [
    { label: "Andijan", value: "Andijan" },
    { label: "Bukhara", value: "Bukhara" },
    { label: "Fergana", value: "Fergana" },
    { label: "Jizzakh", value: "Jizzakh" },
    { label: "Karakalpakstan", value: "Karakalpakstan" },
    { label: "Kashkadaryo", value: "Kashkadaryo" },
    { label: "Khorazm", value: "Khorazm" },
    { label: "Namangan", value: "Namangan" },
    { label: "Navoi", value: "Navoi" },
    { label: "Samarkand", value: "Samarkand" },
    { label: "Sirdaryo", value: "Sirdaryo" },
    { label: "Surkhondaryo", value: "Surkhondaryo" },
    { label: "Tashkent", value: "Tashkent" },
    { label: "Tashkent Region", value: "Tashkent Region" },
  ];
  const handleRegionChange = (e) => {
    setEnquiryDetails({ ...enquiryDetails, region: e.value });
  };

  const paymentOptions = [
    { label: "Test fee", value: "Test fee" },
    { label: "Transfer", value: "Transfer" },
    { label: "Extra TRF", value: "Extra TRF" },
    { label: "EOR (re-mark)", value: "EOR (re-mark)" },
    { label: "OSR", value: "OSR" },
  ];
  const handlePaymentOptionChange = async (e) => {
    setEnquiryDetails({ ...enquiryDetails, module: e.value });

    const price = await axios.get(
      `/api/price?exam=${examUi}&module=${e.value}`,
    );

    // a promocode is scoped to exam + module, so changing module drops it
    clearPromocode();
    checkPromoAvailability(examUi, e.value);
    setBasePrice(price.data.priceAmount);
    setPrice(buildPrice(price.data.priceAmount));
    // setShowCard(true);
    setShowPayButtons(true);
  };

  const handlePrePayment = (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    // console.log(cardDetails);
    let cardNumber = cardDetails.number.replace(/\s/g, "");
    if (cardNumber.length !== 16) {
      setPaymentLoading(false);
      return toast.error(t("invalidCardNumber"));
    }
    if (paymentMethod === "globalpay" && cardDetails.cvc.length < 3) {
      setPaymentLoading(false);
      return toast.error(t("cvcRequired"));
    }
    let year = cardDetails.expiry.split("/")[1];
    let month = cardDetails.expiry.split("/")[0];
    const timeInGMT5 = momentTz.tz("Asia/Karachi").format("DD/MM/YYYY");
    if (paymentMethod === "upay") {
      window.location.reload(true);
      if (cardDetails.phone.length !== 9) {
        setPaymentLoading(false);
        return toast.error(t("invalidPhone9"));
      }
      axios
        .post(`/api/payment/upay/pre`, {
          phoneNumber: cardDetails.phone,
          cardNumber: cardNumber,
          cardExpire: `${year}${month}`,
          enquiryId: `${enquiryDetails.pinpp}:${enquiryDetails.passportId || enquiryDetails.pinpp}:${
            enquiryDetails.firstName
          } ${enquiryDetails.lastName}:${moment(
            enquiryDetails.examDate || examDate,
          ).format("DD.MM.YYYY")}:${enquiryDetails.region}:${
            enquiryDetails.exam
          }`,
          amount: price.upay.total,
          // amount: 1000,
        })
        .then((result) => {
          if (result.status === 200) {
            if (!result.data.success) {
              toast.error(result.data.description);
            } else {
              setTransactionId(result.data.transactionId);
              setIsPost(true);
            }
          } else {
            setErrorMessage(result.data);
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.description || t("genericError"));
        })
        .finally(() => {
          setPaymentLoading(false);
        });
    } else if (paymentMethod === "globalpay") {
      axiosPayment
        .post("/api/globalpay/pay", {
          ...enquiryDetails,
          examDate: moment(enquiryDetails.examDate || examDate).format(
            "DD/MM/YYYY",
          ),
          dob: dob ? moment(dob).format("DD/MM/YYYY") : undefined,
          price: basePrice,
          amount: price.globalpay.total,
          promocode: appliedPromo?.code,
          card: {
            cardNumber,
            expiryDate: cardDetails.expiry,
            cardHolderName: cardDetails.name,
            cardSecurityCode: cardDetails.cvc,
          },
        })
        .then((result) => {
          if (result.data?.requires3ds && result.data.paymentRedirectUrl) {
            // Visa and Mastercard finish on the issuer's 3-D Secure page.
            window.location.replace(`${result.data.paymentRedirectUrl}`);
          } else if (result.data?.paid) {
            navigate(`/verification/${result.data.enquiryId}`);
          } else {
            toast.error(t("genericError"));
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.[i18n.language] || t("genericError"));
        })
        .finally(() => {
          setPaymentLoading(false);
        });
    } else if (paymentMethod === "via") {
      axiosPayment
        .post("/api/via/pre", {
          cardNumber: cardNumber,
          cardExpire: `${month}${year}`,
          enquiryId: ` VIA ${timeInGMT5} eID:${
            enquiryId || enquiryDetails.enquiryId
          } ID:${enquiryDetails.pinpp}:${enquiryDetails.passportId || enquiryDetails.pinpp}:${
            enquiryDetails.firstName
          } ${enquiryDetails.lastName}:${moment(
            enquiryDetails.examDate || examDate,
          ).format("DD.MM.YYYY")}:${enquiryDetails.region}:${
            enquiryDetails.exam
          }`,
          amount: price[paymentMethod].exam * 100,
          // amount: 100000,
          ...promoPayload(),
        })
        .then((result) => {
          if (result.status === 200) {
            setTransactionId(result.data.verifyId);
            setCardPhone(result.data.phone);
            // the server holds the code until this payment is confirmed
            setPromoReservationId(result.data.promocode?.reservationId || null);
            setIsPost(true);
          } else {
            setErrorMessage(result.data);
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.[i18n.language] || t("genericError"));
        })
        .finally(() => {
          setPaymentLoading(false);
        });
    }
  };

  const handlePostPayment = async (e) => {
    e.preventDefault();
    // console.log(cardDetails);
    if (cardDetails.otp === "") {
      // returning before the request used to leave the Pay button disabled
      return toast.error(t("otpRequired"));
    }
    setPaymentLoading(true);
    if (paymentMethod === "upay") {
      axios
        .post(`/api/payment/upay/post`, {
          transactionId,
          otp: cardDetails.otp,
          enquiryId,
          examDate: moment(examDate).format("DD/MM/YYYY"),
          ...enquiryDetails,
          candidateName: `${enquiryDetails.firstName} ${enquiryDetails.lastName}`,
          candidatePassport: enquiryDetails.passportId || enquiryDetails.pinpp,
          candidateDob: dob ? moment(dob).format("DD/MM/YYYY") : undefined,
        })
        .then((result) => {
          if (result.status === 200) {
            setShowSuccess(true);
            setIsPost(false);
            setShowPaymentForm(false);
            setTransactionDetails(result.data.transaction);
            navigate(`/verification/${result.data.transaction.transactionId}`);
          } else {
            setErrorMessage(result.data);
          }
        })
        .catch((err) => {
          toast.error(
            err.response?.data?.data?.description || t("genericError"),
          );
        })
        .finally(() => {
          setPaymentLoading(false);
        });
    } else if (paymentMethod === "via") {
      try {
        let result = await axiosPayment.post("/api/via/post", {
          id: transactionId,
          code: cardDetails.otp,
          enquiryId,
          examDate: moment(examDate).format("DD/MM/YYYY"),
          ...enquiryDetails,
          candidateName: `${enquiryDetails.firstName} ${enquiryDetails.lastName}`,
          candidatePassport: enquiryDetails.passportId || enquiryDetails.pinpp,
          candidateDob: dob ? moment(dob).format("DD/MM/YYYY") : undefined,
          amount: price[paymentMethod].exam,
          promoReservationId,
        });

        if (result.status === 200) {
          setShowSuccess(true);
          setIsPost(false);
          setShowPaymentForm(false);
          setTransactionDetails(result.data.transaction);
          navigate(`/verification/${result.data.transaction.transactionId}`);
        } else {
          setErrorMessage(result.data);
        }
      } catch (error) {
        toast.error(error.response?.data?.[i18n.language] || t("genericError"));
      }
      setPaymentLoading(false);
    }
  };

  const showOneIdLockedError = (data) => {
    const instructionUrl =
      "https://telegra.ph/Malumotlarga-kirishni-qanday-sozlaysiz-02-09";
    const errors = data?.responseCode?.errors;
    const responseCode = data?.responseCode?.code;

    if (responseCode !== 423 || !Array.isArray(errors) || errors.length === 0) {
      return false;
    }

    toast.error(
      <div>
        <div>
          {errors.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </div>
        <div className="mt-2">
          {t("instructions")}:{" "}
          <a href={instructionUrl} target="_blank" rel="noopener noreferrer">
            {instructionUrl}
          </a>
        </div>
      </div>,
      { autoClose: false },
    );

    return true;
  };

  const handleGetPin = (e) => {
    e.preventDefault();
    if (residence === "Uzbekistan") {
      // For Uzbek citizens, require PINPP and call GNK lookup
      if (validate.pinppState !== "has-success" || !enquiryDetails.pinpp) {
        // show the field error instead of silently doing nothing
        setValidate({ ...validate, pinppState: "has-danger" });
        return;
      }
      if (pinLoading) return;
      setPinLoading(true);
      axiosPayment
        .post(
          `/api/pin-info`,
          {
            pinpp: enquiryDetails.pinpp,
          },
          {
            headers: {
              "x-pin-info-secret": process.env.REACT_APP_PIN_INFO_SECRET || "",
            },
          },
        )
        .then((result) => {
          if (result.status === 200) {
            setLoading(false);
            // Auto-fill firstName, lastName, and DOB from GNK response
            setEnquiryDetails({
              ...enquiryDetails,
              firstName: result.data.firstName,
              lastName: result.data.lastName,
              pinpp: result.data.pinpp,
            });
            // Set DOB from response
            if (result.data.dob) {
              const dobParts = result.data.dob.split("/");
              if (dobParts.length === 3) {
                const dobDate = new Date(
                  parseInt(dobParts[2]),
                  parseInt(dobParts[1]) - 1,
                  parseInt(dobParts[0]),
                );
                setDob(dobDate);
              }
            }
            setShowForm(true);
          } else if (result.status === 400 || result.status === 502) {
            toast.error(t("pinppLookupError"));
          }
        })
        .catch((err) => {
          if (!showOneIdLockedError(err.response?.data)) {
            toast.error(t("pinppLookupError"));
          }
        })
        .finally(() => {
          setPinLoading(false);
        });
    } else {
      if (!String(enquiryDetails.passportId || "").trim()) {
        setValidate({ ...validate, passportState: "has-danger" });
        return;
      }
      // For non-Uzbek citizens, automatically set PINPP to "0" and proceed
      setShowForm(true);
      setEnquiryDetails({
        ...enquiryDetails,
        firstName: "",
        lastName: "",
        pinpp: "0",
      });
    }
    setForPayment(true);
  };

  const handleTktDetails = async () => {
    if (!String(enquiryId || "").trim()) {
      toast.error(t("enquiryRequired"));
      return;
    }
    setLoading(true);
    setEnquiryLoading(true);
    try {
      const result = await axios.get(`/api/enquiry-details/${enquiryId}`);
      setEnquiryDetails(result.data);
      if (result.data.status === "Expired") {
        setForPayment(false);
        setReadyToPay(true);
        toast.warn(t("enquiryExpired"));
      } else if (result.data.status === "Paid") {
        setForPayment(false);
        axios.get(`/api/payment-details/${enquiryId}`).then((res) => {
          if (res.status === 200) {
            setTransactionDetails(res.data);
            navigate(`/verification/${res.data.transactionId}`);
          }
        });
      } else {
        setForPayment(true);
        const price = await axios.get(
          `/api/price?exam=${result.data.exam}&module=${result.data.module}`,
        );

        clearPromocode();
        checkPromoAvailability(result.data.exam, result.data.module);
        setBasePrice(price.data.priceAmount);
        setPrice(buildPrice(price.data.priceAmount));
        setReadyToPay(true);
        setShowCard(true);
      }
    } catch (error) {
      toast.error(
        error.response?.status === 404
          ? t("enquiryNotFound")
          : error.response?.data?.[i18n.language] || t("genericError"),
      );
    } finally {
      setLoading(false);
      setEnquiryLoading(false);
    }
  };

  const handlePayMethodButton = async (method) => {
    const resolvedExamDate = examDate || enquiryDetails.examDate;
    if (!resolvedExamDate) {
      toast.error(t("examDateRequired"));
      return;
    }

    setPaymentMethod(method);
    if (method === "xazna") {
      setXaznaLoading(true);
      try {
        const result = await axiosPayment.post(`/api/ielts-enquiry/new`, {
          ...enquiryDetails,
          examDate: moment(resolvedExamDate).format("DD/MM/YYYY"),
          dob: dob ? moment(dob).format("DD/MM/YYYY") : undefined,
          price: basePrice,
          promocode: appliedPromo?.code,
        });
        if (result.status === 200) {
          // console.log(result.data);
          window.location.replace(
            `https://pay.xazna.uz/billing/universal?merchantId=7fa8652e-b9a5-49e5-a50f-e1819f68e7a9&invoice=${result.data.newEnquiry.enquiryId}`,
          );
        } else {
          toast.error(t("genericError"));
        }
      } catch (error) {
        toast.error(error.response?.data?.[i18n.language] || t("genericError"));
      } finally {
        setXaznaLoading(false);
      }
    } else if (method === "uzum") {
      setUzumLoading(true);
      try {
        const result = await axiosPayment.post(`/api/uzum-reg`, {
          ...enquiryDetails,
          examDate: moment(resolvedExamDate).format("DD/MM/YYYY"),
          dob: dob ? moment(dob).format("DD/MM/YYYY") : undefined,
          price: basePrice,
          amount: price.uzum.total,
          promocode: appliedPromo?.code,
        });
        if (result.status === 200) {
          // console.log(result.data);
          window.location.replace(`${result.data.paymentRedirectUrl}`);
        } else {
          toast.error(t("genericError"));
        }
      } catch (error) {
        toast.error(error.response?.data?.[i18n.language] || t("genericError"));
      } finally {
        setUzumLoading(false);
      }
    } else if (
      method === "upay" ||
      method === "via" ||
      method === "globalpay"
    ) {
      // GlobalPay is a Back2Back integration: the card is entered here and
      // charged through our backend, rather than on a hosted page.
      setShowCard(true);
    }
  };

  useEffect(() => {
    if (enquiryId) {
      handleTktDetails();
    }
  }, []);

  return (
    <PublicLayout>
      <div className={forPayment ? "col-12" : "col-12 col-lg-8 mx-auto"}>
        {showPaymentForm && (
          <div className="card mt-2 public-card">
            <div className="card-body text-left">
              <Row>
                {!readyToPay ? (
                  <Col md="6">
                    <form noValidate onSubmit={handleDetailsSubmit}>
                      <h2 className="form-heading">{t("fillDetails")}</h2>
                      <FormGroup>
                        <Label for="exam-select">{t("whichTest")}</Label>
                        <Select
                          inputId="exam-select"
                          className="react-select"
                          classNamePrefix="react-select"
                          placeholder={t("selectPlaceholder")}
                          value={
                            examOptions.find((o) => o.value === examUi) || null
                          }
                          name="exam"
                          options={examOptions}
                          onChange={(e) => {
                            setExamUi(e.value);
                            if (e.value === "IELTS") {
                              setEnquiryId("ielts");
                            }
                            setEnquiryDetails({
                              ...enquiryDetails,
                              exam: e.value,
                            });
                          }}
                        />
                      </FormGroup>
                      {examUi &&
                        (examUi === "IELTS" ? (
                          <>
                            <FormGroup>
                              <Label for="residence-select">
                                {t("residenceCountry")}
                              </Label>
                              <Select
                                inputId="residence-select"
                                className="react-select"
                                classNamePrefix="react-select"
                                value={residenceOptions.find(
                                  (o) => o.value === residence,
                                )}
                                name="residence"
                                isDisabled={showForm}
                                options={residenceOptions}
                                onChange={handleResidenceChange}
                              />
                            </FormGroup>
                            {residence === "Uzbekistan" ? (
                              <FormGroup>
                                <Label for="pinpp">{t("pinpp")}</Label>
                                <Input
                                  id="pinpp"
                                  required
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  name="pinpp"
                                  maxLength="14"
                                  valid={validate.pinppState === "has-success"}
                                  invalid={validate.pinppState === "has-danger"}
                                  value={enquiryDetails.pinpp || ""}
                                  disabled={showForm}
                                  onChange={(e) => {
                                    validatePinpp(e);
                                    handleValuesChange(e);
                                  }}
                                />
                                <FormFeedback>{t("invalidPinpp")}</FormFeedback>
                              </FormGroup>
                            ) : (
                              <FormGroup>
                                <Label for="passportId">{t("passportNo")}</Label>
                                <Input
                                  id="passportId"
                                  required
                                  type="text"
                                  autoComplete="off"
                                  spellCheck={false}
                                  name="passportId"
                                  invalid={validate.passportState === "has-danger"}
                                  value={enquiryDetails.passportId || ""}
                                  disabled={showForm}
                                  onChange={handleValuesChange}
                                />
                                <FormFeedback>
                                  {t("invalidPassportNo")}
                                </FormFeedback>
                              </FormGroup>
                            )}
                            {residence !== "Uzbekistan" && (
                              <FormGroup>
                                <Label>{t("dob")}</Label>
                                <DatePicker
                                  className={"form-control"}
                                  disabled={showForm}
                                  calendarIcon={
                                    <i
                                      className="simple-icon-calendar"
                                      aria-hidden="true"
                                    />
                                  }
                                  clearIcon={
                                    <i
                                      className="iconsminds-close"
                                      aria-hidden="true"
                                    />
                                  }
                                  calendarAriaLabel={t("dob")}
                                  clearAriaLabel={t("clear")}
                                  dayPlaceholder="DD"
                                  monthPlaceholder="MM"
                                  yearPlaceholder="YYYY"
                                  format="dd/MM/y"
                                  minDate={
                                    new Date(
                                      new Date().setFullYear(
                                        new Date().getFullYear() - 100,
                                      ),
                                    )
                                  }
                                  value={dob ? dob : null}
                                  showLeadingZeros={true}
                                  onCalendarOpen={() => {
                                    if (!dob) {
                                      setDob(
                                        new Date(
                                          new Date().setFullYear(
                                            new Date().getFullYear() - 16,
                                          ),
                                        ),
                                      );
                                    }
                                  }}
                                  shouldOpenCalendar={({ reason }) =>
                                    reason !== "focus"
                                  }
                                  onChange={(v) => {
                                    setDob(v);
                                  }}
                                />
                              </FormGroup>
                            )}
                            {!showForm ? (
                              <button
                                type="submit"
                                className="btn btn-primary btn-lg mb-2"
                                disabled={pinLoading}
                                aria-busy={pinLoading}
                              >
                                {renderSpinner(pinLoading)}
                                {t("proceed")}
                              </button>
                            ) : (
                              <>
                                <FormGroup>
                                  <Label for="firstName">{t("firstName")}</Label>
                                  <Input
                                    id="firstName"
                                    required
                                    type="text"
                                    name="firstName"
                                    autoComplete="given-name"
                                    disabled={residence === "Uzbekistan"}
                                    value={enquiryDetails.firstName || ""}
                                    onChange={handleValuesChange}
                                  />
                                </FormGroup>
                                <FormGroup>
                                  <Label for="lastName">{t("lastName")}</Label>
                                  <Input
                                    id="lastName"
                                    required
                                    type="text"
                                    name="lastName"
                                    autoComplete="family-name"
                                    disabled={residence === "Uzbekistan"}
                                    value={enquiryDetails.lastName || ""}
                                    onChange={handleValuesChange}
                                  />
                                </FormGroup>
                                {residence === "Uzbekistan" && (
                                  <FormGroup>
                                    <Label for="passportId">
                                      {t("passportNo")}
                                    </Label>
                                    <Input
                                      id="passportId"
                                      required
                                      type="text"
                                      autoComplete="off"
                                      spellCheck={false}
                                      name="passportId"
                                      value={enquiryDetails.passportId || ""}
                                      onChange={handleValuesChange}
                                    />
                                  </FormGroup>
                                )}
                                <FormGroup>
                                  <Label for="email">{t("email")}</Label>
                                  <Input
                                    id="email"
                                    required
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    spellCheck={false}
                                    valid={validate.emailState === "has-success"}
                                    invalid={validate.emailState === "has-danger"}
                                    value={enquiryDetails.email || ""}
                                    onChange={(e) => {
                                      validateEmail(e);
                                      handleValuesChange(e);
                                    }}
                                  />
                                  <FormFeedback>{t("invalidEmail")}</FormFeedback>
                                </FormGroup>
                                <FormGroup>
                                  <Label>{t("examDate")}</Label>
                                  <DatePicker
                                    className={"form-control"}
                                    calendarIcon={
                                      <i
                                        className="simple-icon-calendar"
                                        aria-hidden="true"
                                      />
                                    }
                                    clearIcon={
                                      <i
                                        className="iconsminds-close"
                                        aria-hidden="true"
                                      />
                                    }
                                    calendarAriaLabel={t("examDate")}
                                    clearAriaLabel={t("clear")}
                                    dayPlaceholder="DD"
                                    monthPlaceholder="MM"
                                    yearPlaceholder="YYYY"
                                    format="dd/MM/y"
                                    minDate={new Date()}
                                    value={examDate ? examDate : null}
                                    showLeadingZeros={true}
                                    shouldOpenCalendar={({ reason }) =>
                                      reason !== "focus"
                                    }
                                    onChange={(v) => {
                                      setExamDate(v);
                                    }}
                                  />
                                </FormGroup>
                                {examUi === "TRKI" ? null : (
                                  <FormGroup>
                                    <Label for="region-select">
                                      {t("examRegion")}
                                    </Label>
                                    <Select
                                      inputId="region-select"
                                      name="region"
                                      className="react-select"
                                      classNamePrefix="react-select"
                                      placeholder={t("selectPlaceholder")}
                                      options={regionOptions}
                                      onChange={handleRegionChange}
                                    />
                                  </FormGroup>
                                )}
                                <FormGroup>
                                  <Label for="payment-option-select">
                                    {t("paymentFor")}
                                  </Label>
                                  <Select
                                    inputId="payment-option-select"
                                    name="paymentOption"
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    placeholder={t("selectPlaceholder")}
                                    options={[
                                      ...paymentOptions,
                                      ...(enquiryDetails.passportId ===
                                      "AD2500372"
                                        ? [{ label: "Dev", value: "Dev" }]
                                        : []),
                                    ]}
                                    onChange={handlePaymentOptionChange}
                                  />
                                </FormGroup>
                                {showPayButtons && (
                                  <>
                                    {renderPromoBox()}
                                    <h3 className="provider-heading">
                                      {t("selectProvider")}
                                    </h3>
                                    {residence !== "Uzbekistan" &&
                                      renderInternationalProviders()}
                                    {renderLocalProviders()}
                                  </>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <FormGroup>
                              <Label for="enquiryId">{t("enterEnquiry")}</Label>
                              <Input
                                id="enquiryId"
                                required
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                name="enquiryId"
                                onChange={(e) =>
                                  setEnquiryId(e.target.value.trim())
                                }
                              />
                            </FormGroup>
                            <button
                              type="submit"
                              className="btn btn-primary btn-lg mb-2"
                              disabled={enquiryLoading}
                              aria-busy={enquiryLoading}
                            >
                              {renderSpinner(enquiryLoading)}
                              {t("next")}
                            </button>
                          </>
                        ))}
                    </form>
                  </Col>
                ) : (
                  <Col md={forPayment ? "6" : "12"}>
                    <h2 className="form-heading">{t("examDetails")}</h2>
                    {!forPayment && enquiryDetails.status === "Expired" && (
                      <div className="alert alert-warning" role="alert">
                        {t("enquiryExpired")}
                      </div>
                    )}
                    <dl className="detail-list">
                      <div className="detail-list-row">
                        <dt>{t("receipt.name")}</dt>
                        <dd>
                          {enquiryDetails.firstName} {enquiryDetails.lastName}
                        </dd>
                      </div>
                      <div className="detail-list-row">
                        <dt>{t("receipt.exam")}</dt>
                        <dd>{enquiryDetails.exam}</dd>
                      </div>
                      <div className="detail-list-row">
                        <dt>{t("receipt.module")}</dt>
                        <dd>{enquiryDetails.module}</dd>
                      </div>
                      <div className="detail-list-row">
                        <dt>{t("date")}</dt>
                        <dd>
                          {moment(enquiryDetails.examDate).format("DD/MM/YYYY")}
                        </dd>
                      </div>
                      <div className="detail-list-row">
                        <dt>{t("region")}</dt>
                        <dd>{enquiryDetails.region}</dd>
                      </div>
                      <div className="detail-list-row">
                        <dt>{t("status")}</dt>
                        <dd>{enquiryDetails.status}</dd>
                      </div>
                    </dl>
                    {forPayment ? (
                      <>
                        {enquiryDetails.pinpp === "0" &&
                          renderInternationalProviders()}
                        {renderPromoBox()}
                        {renderPriceLines()}
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={resetEnquiry}
                      >
                        {t("checkAnotherEnquiry")}
                      </button>
                    )}
                  </Col>
                )}
                {showCard ? (
                  <Col md="6">
                    <div className="card-form mx-auto">
                      {examUi === "IELTS" ? renderPriceLines() : null}
                      <h2 className="form-heading">{t("cardDetails")}</h2>
                      <Cards
                        number={cardDetails.number}
                        expiry={cardDetails.expiry}
                        cvc={cardDetails.cvc}
                        name={cardDetails.name}
                        focused={cardDetails.focus}
                        placeholders={{ name: "" }}
                        acceptedCards={["uzcard", "humo"]}
                        preview={isPost}
                      />
                      <form noValidate onSubmit={handleCardSubmit}>
                        <div className="form-group mb-3 mt-4">
                          <Label for="card-number">{t("cardNo")}</Label>
                          <input
                            id="card-number"
                            type="text"
                            inputMode="numeric"
                            autoComplete="cc-number"
                            name="number"
                            className="form-control"
                            placeholder="0000 0000 0000 0000"
                            pattern="[\d| ]{16,22}"
                            required
                            value={cardDetails.number}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            disabled={isPost}
                          />
                        </div>
                        <div className="form-group mb-3">
                          <Label for="card-expiry">{t("validThru")}</Label>
                          <input
                            id="card-expiry"
                            type="text"
                            inputMode="numeric"
                            autoComplete="cc-exp"
                            name="expiry"
                            className="form-control"
                            placeholder="MM/YY"
                            pattern="\d\d/\d\d"
                            required
                            value={cardDetails.expiry}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            disabled={isPost}
                          />
                        </div>
                        {paymentMethod === "globalpay" && (
                          <div className="form-group mb-3">
                            <Label for="card-cvc">{t("cvc")}</Label>
                            <input
                              id="card-cvc"
                              type="password"
                              inputMode="numeric"
                              autoComplete="cc-csc"
                              name="cvc"
                              className="form-control"
                              maxLength={4}
                              required
                              value={cardDetails.cvc}
                              onChange={handleInputChange}
                              onFocus={handleInputFocus}
                              disabled={isPost}
                            />
                          </div>
                        )}
                        {paymentMethod === "upay" && (
                          <div className="form-group mb-3">
                            <Label for="card-phone">{t("phoneNo")}</Label>
                            <input
                              id="card-phone"
                              type="tel"
                              autoComplete="tel-national"
                              name="phone"
                              className="form-control"
                              required
                              value={cardDetails.phone}
                              onChange={handleInputChange}
                              disabled={isPost}
                              data-tooltip-id="phone"
                              data-tooltip-content="Connected to your card, without 998. e.g. 943213322"
                            />
                            <Tooltip id="phone" />
                          </div>
                        )}
                        {isPost ? (
                          <div className="form-group mb-3">
                            <Label for="card-otp">{t("enterCode")}</Label>
                            <input
                              id="card-otp"
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              name="otp"
                              className="form-control"
                              required
                              autoFocus
                              value={cardDetails.otp}
                              onChange={handleInputChange}
                              aria-describedby={
                                paymentMethod === "via" ? "otp-help" : undefined
                              }
                            />
                            {paymentMethod === "via" && (
                              <small id="otp-help" className="form-text text-muted">
                                {t("codeSent")} {cardPhone}
                              </small>
                            )}
                          </div>
                        ) : null}
                        <button
                          type="submit"
                          className="btn btn-primary btn-lg btn-block mb-2"
                          disabled={paymentLoading}
                          aria-busy={paymentLoading}
                        >
                          {renderSpinner(paymentLoading)}
                          {isPost ? t("pay") : t("proceed")}
                        </button>
                      </form>
                    </div>
                    <div className="secure-note mt-3">
                      <i className="simple-icon-lock" aria-hidden="true" />
                      <p className="mb-0">
                        <strong>{t("securePaymentTitle")}</strong>{" "}
                        {t("securePayment1")} <strong>{providerName}</strong>,{" "}
                        {t("securePayment2")}
                      </p>
                    </div>
                  </Col>
                ) : null}
              </Row>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};


export default Home;
